import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'framework/ui/ui-builder';
import { CreateForm } from 'framework/form';
import { Box, Typography } from '@mui/material';
import { Button } from 'framework/ui/mui-builder';
import Add from '@mui/icons-material/Add';
import {
  HealthZone,
  STAFFING_RISK_PRIORITY,
  STAFFING_RISK_STATUS_TYPE,
  StaffingRiskIssue,
} from 'domain/shared/staffing';
import StaffingRiskIssueItem from './components/StaffingRiskIssueItem';
import { STAFFING_RISK_STATUS } from 'domain/shared/staffing';

type ExtendedStaffingRiskIssue = StaffingRiskIssue & { isNew?: boolean };

interface StaffingRiskIssuesModalProps {
  isOpen: boolean;
  onClose: () => void;
  data?: any;
  submitFormFunc?: (req: any) => Promise<void>;
  globalData?: any;
}

const StaffingRiskIssuesModal: React.FC<StaffingRiskIssuesModalProps> = ({
  isOpen,
  onClose,
  data,
  submitFormFunc,
  globalData,
}) => {
  const projectData = data?.projectData;
  const [issues, setIssues] = useState<ExtendedStaffingRiskIssue[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const initialFormData = useMemo(
    () => ({
      issues: [],
    }),
    [],
  );

  const createIssue = useCallback(
    (issueData: Partial<StaffingRiskIssue> = {}, isNew = false): ExtendedStaffingRiskIssue => ({
      id: issueData.id || '',
      description: issueData.description || '',
      status: issueData.status || (STAFFING_RISK_STATUS.ACTIVE as STAFFING_RISK_STATUS_TYPE),
      targetPriority: issueData.targetPriority || STAFFING_RISK_PRIORITY.SAFE,
      systemIssueType: issueData.systemIssueType || undefined,
      staffingRiskId: issueData.staffingRiskId || '',
      createdAt: issueData.createdAt || new Date().toISOString(),
      updatedAt: issueData.updatedAt,
      isNew,
    }),
    [],
  );

  const getIssuesFromData = useCallback((): ExtendedStaffingRiskIssue[] => {
    const staffingRiskIssues = projectData?.staffingRisk?.issues;
    if (Array.isArray(staffingRiskIssues) && staffingRiskIssues.length > 0) {
      return staffingRiskIssues.map((issue: any) =>
        createIssue(
          {
            id: issue.id,
            description: issue.description,
            status: issue.status,
            targetPriority: issue.targetPriority,
            systemIssueType: issue.systemIssueType,
            staffingRiskId: issue.staffingRiskId,
            createdAt: issue.createdAt,
            updatedAt: issue.updatedAt,
          },
          false,
        ),
      );
    }
    return [createIssue({}, true)];
  }, [projectData, createIssue]);

  useEffect(() => {
    if (isOpen) {
      setIssues(getIssuesFromData());
    }
  }, [isOpen, getIssuesFromData]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const validIssues = issues.filter(issue => issue.description.trim() !== '');
      if (validIssues.length === 0) {
        setSubmitError('Please add at least one issue with description');
        setIsSubmitting(false);
        return;
      }

      const submitData = {
        id: projectData?.id,
        issues: validIssues.map(issue => ({
          id: issue.isNew ? null : issue.id,
          description: issue.description,
          status: issue.status,
          targetPriority: issue.targetPriority,
        })),
      };

      if (submitFormFunc) {
        await submitFormFunc(submitData);
        onClose();
      }
    } catch (error) {
      setSubmitError('Failed to submit issues. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addNewIssue = useCallback(() => {
    setIssues(prev => [...prev, createIssue({}, true)]);
  }, [createIssue]);

  const updateIssue = useCallback((index: number, updates: Partial<ExtendedStaffingRiskIssue>) => {
    setIssues(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...updates };
      return updated;
    });
  }, []);

  const removeIssue = useCallback((index: number) => {
    setIssues(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleDescriptionChange = useCallback(
    (index: number, value: string) => {
      updateIssue(index, { description: value });
    },
    [updateIssue],
  );

  const handleStatusChange = useCallback(
    (index: number, status: STAFFING_RISK_STATUS_TYPE) => {
      updateIssue(index, { status });
    },
    [updateIssue],
  );

  const handleZoneChange = useCallback(
    (index: number, zone: HealthZone) => {
      updateIssue(index, { targetPriority: zone });
    },
    [updateIssue],
  );

  return (
    <CreateForm submitFormFunc={handleSubmit} initialData={initialFormData}>
      <Modal isOpen={isOpen} onToggle={onClose} modalSize="sm" hasCancelButton={false} title="Manage issues">
        {issues.length > 0 && (
          <Box>
            {issues.map((issue, index) => (
              <StaffingRiskIssueItem
                key={issue.id || index}
                issue={issue}
                index={index}
                onDescriptionChange={handleDescriptionChange}
                onStatusChange={handleStatusChange}
                onZoneChange={handleZoneChange}
                onRemove={removeIssue}
              />
            ))}
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Button variant="outlined" startIcon={<Add />} onClick={addNewIssue} color="primary">
            Add Issue
          </Button>
        </Box>

        {submitError && (
          <Box mb={2}>
            <Typography color="error" variant="body1">
              {submitError}
            </Typography>
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Modal>
    </CreateForm>
  );
};

export default StaffingRiskIssuesModal;
